/* eslint-disable no-param-reassign */
import { isChild } from '@apia/util';
import { useEffect, ReactNode, useRef, useCallback } from 'react';
import { Box } from '@apia/theme';

export const AutoEllipsis = ({
  children,
  findParent,
  overrideStyles,
}: {
  children: ReactNode;
  /**
   * Si se pasa el método findParent, será invocado con el nodo de AutoEllipsis
   * para dar control sobre cuál es el elemento al que se le clonarán los
   * estilos.
   */
  findParent?: (currentElement: HTMLElement) => HTMLElement;
  /**
   * Si se pasa overrideStyles, será invocado con el nodo actual de
   * AutoEllipsis para tomar decisiones acerca de cuáles estilos serán
   * aplicados.
   */
  overrideStyles?: (
    currentElement: HTMLElement,
  ) => Partial<CSSStyleDeclaration>;
}) => {
  const unsuscribe = useRef(() => {});
  const clone = useRef<HTMLElement | null>(null);
  const timeout = useRef(0);
  const ref = useCallback(
    (el: HTMLElement) => {
      if (!el || !document.contains(el)) return;

      unsuscribe.current();

      function showBox() {
        if (!el || !document.contains(el)) return;

        // If there is any clone open, close it
        document
          .querySelectorAll('.autoellipsis_clone')
          .forEach((c) => c.remove());

        clone.current = el.cloneNode(true) as HTMLElement;
        const target = findParent?.(el) ?? el;
        const styles = window.getComputedStyle(target);
        for (let i = 0; i < styles.length; i++) {
          try {
            const name = styles[i] as keyof typeof target.style;
            const value = styles[name];
            if (!(name as string).match(/webkit/)) {
              (clone.current as HTMLElement).style[name as any] = value as any;
            }
          } catch (e) {
            e;
          }
        }

        const bound = el.getBoundingClientRect();
        clone.current.style.position = 'fixed';
        clone.current.style.left = `${bound.left - 1}px`;
        clone.current.style.top = `${bound.top - 1}px`;
        clone.current.style.width = `${bound.width + 8}px`;
        clone.current.style.whiteSpace = 'break-spaces';
        clone.current.style.overflow = 'visible';
        clone.current.style.textOverflow = 'unset';
        clone.current.style.background =
          'var(--theme-ui-colors-palette-background-paper)';
        clone.current.style.color =
          'var(--theme-ui-colors-palette-text-primary)';
        clone.current.style.border = '1px solid';
        clone.current.style.borderColor =
          'var(--theme-ui-colors-palette-border-field)';
        clone.current.style.zIndex = '1000000';
        clone.current.style.height = 'auto';
        clone.current.style.fill =
          'var(--theme-ui-colors-palette-text-primary)';
        clone.current.style.pointerEvents = 'none';
        clone.current.style.wordBreak = 'break-all';
        clone.current.classList.add('autoellipsis_clone');

        const overridenStyles = overrideStyles?.(el);

        if (overridenStyles) {
          Object.entries(overridenStyles).forEach(([name, value]) => {
            if (name.match(/webkit/)) return;

            try {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
              (clone.current as HTMLElement).style[name as any] =
                value as string;
            } catch (e) {
              e;
            }
          });
        }

        clone.current.addEventListener('mouseleave', (ev) => {
          if (
            isChild(
              ev.relatedTarget as HTMLElement,
              (current) => current === el,
            )
          )
            return;

          clone.current?.remove();
        });

        let interval: any = 0;
        function close() {
          clone.current?.remove();
          document.removeEventListener('scroll', close);
          clearInterval(interval);
        }
        interval = setInterval(() => {
          if (!document.contains(el)) {
            close();
          }
        }, 100);

        document.addEventListener('scroll', close);
        document.body.append(clone.current);

        const cloneBounding = clone.current.getBoundingClientRect();

        const difX = Math.abs(cloneBounding.width - bound.width);
        const difY = Math.abs(cloneBounding.height - bound.height);

        if (difX < 50 && difY < 10) {
          clone.current.remove();
        }
      }

      function hideBox(ev: MouseEvent) {
        if (
          isChild(
            ev.relatedTarget as HTMLElement,
            (current) => current === clone.current,
          )
        )
          return;

        clone.current?.remove();
      }

      function handleMouseEnter() {
        timeout.current = setTimeout(() => {
          showBox();
        }, 500) as unknown as number;
      }
      function handleMouseLeave(ev: MouseEvent) {
        clearTimeout(timeout.current);
        hideBox(ev);
      }

      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);

      unsuscribe.current = () => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
        clearTimeout(timeout.current);
      };
    },
    [findParent, overrideStyles],
  );

  useEffect(() => {
    return () => {
      clone.current?.remove();
    };
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      className="autoellipsis__container"
    >
      {children}
    </Box>
  );
};
